import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Save, ExternalLink, AlertTriangle } from "lucide-react";

// P3-2: provider labels with setup requirements
const PROVIDERS = [
  { value: "mock", label: "Mock — modo de teste (sem hardware)" },
  { value: "unifi", label: "UniFi Controller (requer controlador + credenciais)" },
  { value: "omada", label: "TP-Link Omada (requer Omada Controller externo)" },
  { value: "mikrotik", label: "MikroTik HotSpot (requer RouterOS v7+)" },
];

// P2-4: slug: only lowercase letters, digits, hyphens, max 60 chars
function sanitizeSlug(value) {
  return value.toLowerCase().replace(/[^a-z0-9-]/g, "").substring(0, 60);
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium">{label}</Label>
      {children}
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

export default function WiFiPortalSettings({ settings, onSave, saving }) {
  const [form, setForm] = useState(settings || {});

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setCredential = (key, value) =>
    setForm((f) => ({ ...f, provider_credentials: { ...(f.provider_credentials || {}), [key]: value } }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const portalUrl = form.portal_slug
    ? `${window.location.origin}/wifi/${form.portal_slug}`
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Portal status */}
      <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
        <div>
          <p className="font-medium">Portal ativo</p>
          <p className="text-sm text-gray-500">Clientes veem o formulário de cadastro</p>
        </div>
        <Switch checked={!!form.enabled} onCheckedChange={(v) => set("enabled", v)} />
      </div>

      {/* Branding */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Identidade visual</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Slug do portal" hint="Apenas letras minúsculas, números e hífens. Ex: meu-restaurante">
            <Input
              value={form.portal_slug || ""}
              onChange={(e) => set("portal_slug", sanitizeSlug(e.target.value))}
              placeholder="meu-restaurante"
            />
          </Field>
          <Field label="URL do logo">
            <Input value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." />
          </Field>
          <Field label="Título do portal">
            <Input value={form.portal_title || ""} onChange={(e) => set("portal_title", e.target.value)} placeholder="Bem-vindo ao nosso Wi-Fi!" />
          </Field>
          <Field label="Subtítulo">
            <Input value={form.portal_subtitle || ""} onChange={(e) => set("portal_subtitle", e.target.value)} placeholder="Cadastre-se para ter acesso gratuito" />
          </Field>
          <Field label="Cor principal">
            <div className="flex gap-2 items-center">
              <input type="color" value={form.brand_primary_color || "#FF6B35"} onChange={(e) => set("brand_primary_color", e.target.value)} className="h-9 w-12 rounded border cursor-pointer" />
              <Input value={form.brand_primary_color || ""} onChange={(e) => set("brand_primary_color", e.target.value)} className="flex-1" />
            </div>
          </Field>
          <Field label="Cor de fundo">
            <div className="flex gap-2 items-center">
              <input type="color" value={form.brand_background_color || "#FFFFFF"} onChange={(e) => set("brand_background_color", e.target.value)} className="h-9 w-12 rounded border cursor-pointer" />
              <Input value={form.brand_background_color || ""} onChange={(e) => set("brand_background_color", e.target.value)} className="flex-1" />
            </div>
          </Field>
        </div>
      </section>

      {/* Social & CRM links */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Links sociais</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Instagram URL"><Input value={form.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/..." /></Field>
          <Field label="WhatsApp (número)"><Input value={form.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="+351 912 345 678" /></Field>
          <Field label="Link de check-in"><Input value={form.checkin_url || ""} onChange={(e) => set("checkin_url", e.target.value)} placeholder="https://..." /></Field>
          <Field label="Link Google Review"><Input value={form.google_review_url || ""} onChange={(e) => set("google_review_url", e.target.value)} placeholder="https://g.page/..." /></Field>
        </div>
      </section>

      {/* GDPR & Coupon */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">GDPR & Recompensa</h3>
        <Field label="Texto de consentimento GDPR">
          <Textarea value={form.marketing_consent_text || ""} onChange={(e) => set("marketing_consent_text", e.target.value)} rows={2} placeholder="Autorizo o uso dos meus dados para receber comunicações promocionais..." />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Cupão de desconto (opcional)"><Input value={form.coupon_code || ""} onChange={(e) => set("coupon_code", e.target.value)} placeholder="WIFI10" /></Field>
          <Field label="Mensagem de sucesso"><Input value={form.success_message || ""} onChange={(e) => set("success_message", e.target.value)} placeholder="Wi-Fi liberado! Obrigado." /></Field>
        </div>
      </section>

      {/* WiFi provider */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Provedor WiFi</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Provedor">
            <Select value={form.wifi_provider || "mock"} onValueChange={(v) => set("wifi_provider", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Minutos de sessão">
            <Input type="number" min={1} value={form.session_minutes || 60} onChange={(e) => set("session_minutes", Number(e.target.value))} />
          </Field>
          <Field label="Limite dados (MB, 0=ilimitado)">
            <Input type="number" min={0} value={form.data_limit_mb || 0} onChange={(e) => set("data_limit_mb", Number(e.target.value))} />
          </Field>
        </div>

        {form.wifi_provider !== "mock" && (
          <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              As credenciais são guardadas sem encriptação. Use apenas em ambiente de rede confiável até activar criptografia at-rest.
            </span>
          </div>
        )}

        {form.wifi_provider === "unifi" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-blue-50">
            <Field label="Controller URL"><Input value={form.provider_credentials?.controller_url || ""} onChange={(e) => setCredential("controller_url", e.target.value)} placeholder="https://192.168.1.1:8443" /></Field>
            <Field label="Site ID"><Input value={form.provider_credentials?.site_id || ""} onChange={(e) => setCredential("site_id", e.target.value)} placeholder="default" /></Field>
            <Field label="Username"><Input value={form.provider_credentials?.username || ""} onChange={(e) => setCredential("username", e.target.value)} /></Field>
            <Field label="Password"><Input type="password" value={form.provider_credentials?.password || ""} onChange={(e) => setCredential("password", e.target.value)} /></Field>
          </div>
        )}
        {form.wifi_provider === "omada" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-green-50">
            <Field label="Portal URL"><Input value={form.provider_credentials?.portal_url || ""} onChange={(e) => setCredential("portal_url", e.target.value)} placeholder="https://omada.tplinkcloud.com" /></Field>
          </div>
        )}
        {form.wifi_provider === "mikrotik" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-orange-50">
            <Field label="Router URL"><Input value={form.provider_credentials?.router_url || ""} onChange={(e) => setCredential("router_url", e.target.value)} placeholder="http://192.168.88.1" /></Field>
            <Field label="Username"><Input value={form.provider_credentials?.username || ""} onChange={(e) => setCredential("username", e.target.value)} /></Field>
            <Field label="Password"><Input type="password" value={form.provider_credentials?.password || ""} onChange={(e) => setCredential("password", e.target.value)} /></Field>
          </div>
        )}
      </section>

      <div className="flex items-center gap-3 pt-2">
        <Button type="submit" disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "A guardar..." : "Guardar configurações"}
        </Button>
        {portalUrl && (
          <a href={portalUrl} target="_blank" rel="noopener noreferrer">
            <Button type="button" variant="outline">
              <ExternalLink className="h-4 w-4 mr-2" /> Ver portal
            </Button>
          </a>
        )}
      </div>
    </form>
  );
}
